import { FormConfig } from '@dxb-web-ui-framework/content-composer/components/FormBuilder/types';
import { useTranslation } from 'react-i18next';
import { getClientTimezone } from '@dxb-web-ui-framework/common/utils/Date';
import { getFloatNumber } from '@dxb-web-ui-framework/content-composer/components/FormBuilder/formElements/FloatNumberInput';
import { useLanguage } from '../../../../internationalization/src/contexts/Internationalization/hooks';
import { dateRangeFormatter } from '../../../../content-composer/src/components/Generic/SelectedFiltersChip/ValueFormatters/ValueFormatters';
import { useOrderStatuses } from '../../contexts/Parameters/hooks';
import { getTransactionOrderStatus } from '../TransactionOrderStatusChip';
import { transactionTypeToChipTitleTransformer } from '../../../../content-composer/src/utils';

export const ORDER_STATUS_DEFAULT_VALUE = -1;
export const ORDER_DIRECTION_DEFAULT_VALUE = 0;

//TODO: Import iban field when it is specified
const useConsolidatedTransactionsFiltersConfig = (): FormConfig => {
  const { t } = useTranslation();
  const { cultureName } = useLanguage();
  const orderStatuses = useOrderStatuses();
  const timezone = getClientTimezone();

  return {
    validationSchema: {
      fromAmount: getFloatNumber({ rulesParams: { REGEX: /^[+-]?\d+(\.\d+)|\s?$/ } }),
      toAmount: getFloatNumber({ rulesParams: { REGEX: /^[+-]?\d+(\.\d+)|\s?$/ } }),
    },
    components: [
      {
        key: 'beneficiaryName',
        type: 'text',
        name: 'beneficiaryName',
        label: t('consolidatedTransactionsFiltersDrawer.BeneficiaryName.Label'),
        properties: {
          mb: 3,
        },
      },
      {
        key: 'orderDirection',
        type: 'select',
        name: 'orderDirection',
        label: t('consolidatedTransactionsFiltersDrawer.Type.Label'),
        options: [
          { value: 1, label: t('Filters.Debit') },
          { value: 2, label: t('Filters.Credit') },
        ],
        emptyOption: {
          value: ORDER_DIRECTION_DEFAULT_VALUE,
          label: t('useConsolidatedTransactionsFiltersConfig.EmptyOption.Label'),
        },
        properties: {
          variant: 'filled',
          filters: {
            valueFormatter: (transactionType: number) =>
              transactionType === ORDER_DIRECTION_DEFAULT_VALUE
                ? t('useConsolidatedTransactionsFiltersConfig.EmptyOption.Label')
                : transactionTypeToChipTitleTransformer(t, transactionType),
          },
        },
      },
      {
        key: 'orderStatus',
        type: 'select',
        name: 'orderStatus',
        label: t('consolidatedTransactionsFiltersDrawer.StatusSelect.Label'),
        options:
          orderStatuses.map(status => ({
            value: status.serialNumber,
            label: getTransactionOrderStatus(status.serialNumber, orderStatuses, t).value,
          })) ?? [],
        emptyOption: { value: ORDER_STATUS_DEFAULT_VALUE, label: 'All' },
        properties: {
          variant: 'filled',
          mb: 3,
          filters: {
            valueFormatter: (statusValue: number) =>
              statusValue === ORDER_STATUS_DEFAULT_VALUE
                ? 'All'
                : getTransactionOrderStatus(statusValue, orderStatuses, t).value,
          },
        },
      },
      {
        key: 'transactionDate',
        type: 'datePickerRange',
        name: 'transactionDate',
        label: t('consolidatedTransactionsFiltersDrawer.TransactionDateRangePicker.Label'),
        properties: {
          formatExp: t('global.settings.date.format'),
          localeName: cultureName,
          tz: timezone,
          btnLabel: t('consolidatedTransactionsFiltersDrawer.TransactionDateRangePicker.btnLabel'),
          filters: {
            rangeReference: {
              from: 'fromTrxDate',
              to: 'toTrxDate',
            },
            valueFormatter: (dateRange: { from: string; to: string }) =>
              dateRangeFormatter(dateRange.from, dateRange.to, 'dd-MM-yyyy'),
          },
        },
      },
      {
        key: 'valueDate',
        type: 'datePickerRange',
        name: 'valueDate',
        label: t('consolidatedTransactionsFiltersDrawer.ValueDateRangePicker.Label'),
        properties: {
          formatExp: t('global.settings.date.format'),
          localeName: cultureName,
          tz: timezone,
          mt: 3,
          btnLabel: t('consolidatedTransactionsFiltersDrawer.ValueDateRangePicker.btnLabel'),
          filters: {
            rangeReference: {
              from: 'valueDateFrom',
              to: 'valueDateTo',
            },
            valueFormatter: (dateRange: { from: string; to: string }) =>
              dateRangeFormatter(dateRange.from, dateRange.to, 'dd-MM-yyyy'),
          },
        },
      },
      {
        type: 'fieldSet',
        key: 'fieldSet',
        label: 'Amount',
        components: [
          {
            key: 'fromAmount',
            type: 'floatNumber',
            name: 'fromAmount',
            label: t('consolidatedTransactionsFiltersDrawer.FromAmountTextField.Label'),
            properties: {
              variant: 'filled',
              allowNegative: false,
              mr: 2,
              inputProps: { onPaste: (e: ClipboardEvent) => e.preventDefault(), autoComplete: 'off' },
            },
          },
          {
            key: 'toAmount',
            type: 'floatNumber',
            name: 'toAmount',
            label: t('consolidatedTransactionsFiltersDrawer.ToAmountTextField.Label'),
            properties: {
              variant: 'filled',
              allowNegative: false,
              inputProps: { onPaste: (e: ClipboardEvent) => e.preventDefault(), autoComplete: 'off' },
            },
          },
        ],
        properties: {
          isRow: true,
          shouldGroupValidation: false,
          filters: {
            rangeReference: {
              from: 'fromAmount',
              to: 'toAmount',
            },
            valueFormatter: (amountRange: { from: number; to: number }) =>
              `${amountRange.from ?? ''} - ${amountRange.to ?? ''}`,
          },
        },
      },
    ],
  };
};

export { useConsolidatedTransactionsFiltersConfig };
